import AddIcon from '@mui/icons-material/Add';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FilterIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SortIcon from '@mui/icons-material/SortByAlpha';
import {
	Box,
	Button,
	Divider,
	Fab,
	IconButton,
	InputAdornment,
	List,
	MenuItem,
	Paper,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks';
import { useI18N } from '../i18n';
import { useAuth } from '../redux/slices/auth';
import {
	getTicketUser,
	getAllTickets,
	useTickets,
	FIXED_TICKET_STATES,
} from '../redux/slices/tickets';
import { StyledMenu } from './StyledMenu';
import { TicketsListItem } from './TicketsListItem';
import type { NewTicketData, TicketsApiDataItem, TicketStatus } from '../redux/slices/tickets';
import { useDispatch } from 'react-redux';
import { useRedux } from '../redux';
import { KeyboardArrowDown, WhatsApp } from '@mui/icons-material';
import UpdatePhoneModal from './UpdatePhoneModal';

type SearchType = 'title' | 'email' | 'cnpj' | 'ticketNumber';

export interface TicketsListFilter {
	key: keyof NewTicketData;
	value: unknown;
	comparisonFn?: (ticket: NewTicketData) => boolean;
}

export interface TicketsListSort {
	key: keyof NewTicketData;
	asc: boolean;
}

export const TicketsList = (): JSX.Element => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const i18n = useI18N();
	const isMobile = useIsMobile();
	const { user, company, isTaugorCompany } = useAuth();
	const { tickets, lastVisibleDate, hasMore, loading } = useTickets();
	const { tickets: ticketsActions } = useRedux();

	const [visibleTickets, setVisibleTickets] = useState<NewTicketData[]>([]);
	const [searchBy, setSearchBy] = useState<SearchType>('title');
	const [searchText, setSearchText] = useState('');
	const [filters, setFilters] = useState<TicketsListFilter[] | null>(null);
	const [sort, setSort] = useState<TicketsListSort>({ key: 'lastChangeDate', asc: false });
	const [isFilterMenuOpen, setFilterMenuOpen] = useState(false);
	const [isSortMenuOpen, setSortMenuOpen] = useState(false);
	const [showPhoneModal, setShowPhoneModal] = useState(false);

	const [changeTickets, setChangeTickets] = useState<'AllTickets' | 'UserTickets'>('AllTickets');

	const filterAnchorRef = useRef<HTMLButtonElement>(null);
	const sortAnchorRef = useRef<HTMLButtonElement>(null);
	const cnpjRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (searchBy === 'cnpj' && cnpjRef.current) {
			Inputmask('99.999.999/9999-99').mask(cnpjRef.current);
		}
	}, [searchBy]);

	useEffect(() => {
		if (!isTaugorCompany) {
			if (user) dispatch(getTicketUser(user ?? ''));
		} else {
			dispatch(getAllTickets({ pageSize: 10, lastVisibleDate: lastVisibleDate ?? undefined }));
		}

		return () => {
			dispatch(ticketsActions.resetTickets());
		};
	}, [dispatch]);

	const handleChangeTickets = (
		_: React.MouseEvent<HTMLElement>,
		newChangeTicket: 'AllTickets' | 'UserTickets' | null
	) => {
		if (newChangeTicket !== null) {
			setChangeTickets(newChangeTicket);

			if (newChangeTicket === 'AllTickets') {
				dispatch(ticketsActions.resetTickets());
				dispatch(getAllTickets({ pageSize: 10 }));
			} else {
				if (user) dispatch(getTicketUser(user ?? ''));
			}
		}
	};

	const handleLoadMore = () => {
		if (hasMore && !loading) {
			dispatch(getAllTickets({ pageSize: 25, lastVisibleDate: lastVisibleDate ?? undefined }));
		}
	};

	const handleFilterClick = (state: TicketsApiDataItem | null) => {
		setFilters((prevFilters) => {
			if (state) {
				const filters = prevFilters || [];
				const existingFilterIndex = filters.findIndex(
					(filter) => filter.key === 'status' && filter.value === state.name
				);

				if (existingFilterIndex > -1) {
					filters.splice(existingFilterIndex, 1);
					return [...filters];
				} else {
					return [
						...filters,
						{
							key: 'status',
							value: state.name,
							comparisonFn: (ticket) => ticket.status === state.name,
						},
					];
				}
			}

			return null;
		});
	};

	const handleSortClick = (key: keyof NewTicketData, asc: boolean) => {
		setSort({ key, asc });
		setSortMenuOpen(false);
	};

	useEffect(() => {
		const newFilters: TicketsListFilter[] = FIXED_TICKET_STATES.filter(
			(state) => state.name !== 'Finalizado'
		).map((state) => ({
			key: 'status',
			value: state.name,
			comparisonFn: (ticket) => ticket.status === state.name,
		}));

		setFilters(newFilters);
	}, []);

	useEffect(() => {
		let newVisibleTickets = [...tickets];

		// Aplica filtros
		if (filters) {
			newVisibleTickets = newVisibleTickets.filter((ticket) => {
				for (const filter of filters) {
					if (filter.comparisonFn && filter.comparisonFn(ticket)) {
						return true;
					}

					if (filter.key === 'status' && ticket.status === filter.value) {
						return true;
					}
				}

				return false;
			});
		}

		// Aplica pesquisa
		if (searchText) {
			newVisibleTickets = newVisibleTickets.filter((ticket) => {
				const target =
					searchBy === 'title'
						? ticket.title
						: searchBy === 'cnpj'
						? ticket.associatedUser?.cnpj.replace(/\D/g, '') || ticket.cnpj
						: searchBy === 'ticketNumber'
						? ticket.number
						: ticket.associatedUser?.email || ticket.email;

				if (searchBy === 'cnpj') {
					return target?.includes(searchText.replace(/\D/g, ''));
				}

				return target?.toLowerCase().includes(searchText.toLowerCase());
			});
		}

		// Aplica ordenação
		const sortMultiplier = sort.asc ? 1 : -1;

		newVisibleTickets = newVisibleTickets.sort((ticketA, ticketB) => {
			const valueA = ticketA[sort.key as keyof NewTicketData] ?? 0;
			const valueB = ticketB[sort.key as keyof NewTicketData] ?? 0;

			if (typeof valueA === 'string' && typeof valueB === 'string') {
				return (
					valueA.localeCompare(valueB, undefined, {
						sensitivity: 'base',
						numeric: sort.key === 'number',
					}) * sortMultiplier
				);
			}

			if (valueA && valueB) {
				return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * sortMultiplier;
			}

			return valueA ? -1 * sortMultiplier : valueB ? 1 * sortMultiplier : 0;
		});

		// Atualiza os tickets visíveis
		setVisibleTickets(newVisibleTickets);
	}, [searchText, filters, sort, tickets]);

	return (
		<Paper
			elevation={6}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					gap: 2,
					p: isMobile ? 2 : 4,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 1,
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="h5">{i18n('tickets.list')}</Typography>

						<Tooltip title={i18n('tickets.list.refresh')}>
							<Box component="span">
								<IconButton
									color="primary"
									disabled={loading}
									onClick={() => {
										if (changeTickets === 'AllTickets' && isTaugorCompany) {
											dispatch(ticketsActions.resetTickets());
											dispatch(getAllTickets({ pageSize: 10 }));
										} else {
											if (user) dispatch(getTicketUser(user ?? ''));
										}
									}}
								>
									<RefreshIcon />
								</IconButton>
							</Box>
						</Tooltip>
					</Box>

					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography variant="body2">{i18n('tickets.notificationWhatapp')}</Typography>
						<Tooltip title={i18n('tickets.list.whatsappPhone')}>
							<IconButton
								color="default"
								sx={{ backgroundColor: '#25D366' }}
								onClick={() => setShowPhoneModal(true)}
							>
								<WhatsApp />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>

				<UpdatePhoneModal open={showPhoneModal} onClose={() => setShowPhoneModal(false)} />

				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 0.5,
						}}
					>
						<TextField
							select
							size="small"
							label="Buscar por"
							value={searchBy}
							onChange={(e) => {
								setSearchBy(e.target.value as 'title' | 'email' | 'cnpj' | 'ticketNumber');
								if (cnpjRef.current) {
									Inputmask.remove(cnpjRef.current);
								}
							}}
						>
							<MenuItem value="title">Título do ticket</MenuItem>
							<MenuItem value="ticketNumber">Número do ticket</MenuItem>
							<MenuItem value="cnpj">CNPJ do usuário</MenuItem>
							<MenuItem value="email">Email do usuário</MenuItem>
						</TextField>

						{searchBy === 'cnpj' ? (
							<TextField
								id="title"
								inputRef={cnpjRef}
								label={'Digite o CNPJ'}
								value={searchText}
								required
								disabled={loading}
								onChange={(e) => {
									setSearchText(e.target.value);
								}}
							/>
						) : (
							<TextField
								id="search"
								size="small"
								label={i18n('tickets.list.search')}
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<SearchIcon />
										</InputAdornment>
									),
								}}
							/>
						)}

						<Tooltip title={i18n('tickets.list.filter')}>
							<IconButton
								ref={filterAnchorRef}
								onClick={() => setFilterMenuOpen(!isFilterMenuOpen)}
							>
								<FilterIcon />
							</IconButton>
						</Tooltip>

						<StyledMenu
							open={isFilterMenuOpen}
							anchorEl={filterAnchorRef.current || null}
							onClose={() => setFilterMenuOpen(false)}
						>
							<MenuItem selected={!filters} onClick={() => handleFilterClick(null)}>
								{i18n('tickets.list.filter.all')}
							</MenuItem>

							{FIXED_TICKET_STATES.map((state) => (
								<MenuItem
									key={state.id}
									selected={
										!!filters &&
										filters.some((filter) => filter.key === 'status' && filter.value === state.name)
									}
									onClick={() => handleFilterClick(state)}
								>
									{i18n('tickets.list.filter.status')} - {state.name}
								</MenuItem>
							))}
						</StyledMenu>

						<Tooltip title={i18n('tickets.list.sort')}>
							<IconButton ref={sortAnchorRef} onClick={() => setSortMenuOpen(!isSortMenuOpen)}>
								<SortIcon />
							</IconButton>
						</Tooltip>

						<StyledMenu
							open={isSortMenuOpen}
							anchorEl={sortAnchorRef.current}
							onClose={() => setSortMenuOpen(false)}
						>
							<MenuItem
								selected={sort.key === 'number' && sort.asc}
								onClick={() => handleSortClick('number', true)}
							>
								{i18n('tickets.list.sort.number')} - {i18n('tickets.list.sort.ascending')}
							</MenuItem>

							<MenuItem
								selected={sort.key === 'number' && !sort.asc}
								onClick={() => handleSortClick('number', false)}
							>
								{i18n('tickets.list.sort.number')} - {i18n('tickets.list.sort.descending')}
							</MenuItem>

							<MenuItem
								selected={sort.key === 'title' && sort.asc}
								onClick={() => handleSortClick('title', true)}
							>
								{i18n('tickets.list.sort.subject')} - {i18n('tickets.list.sort.ascending')}
							</MenuItem>

							<MenuItem
								selected={sort.key === 'title' && !sort.asc}
								onClick={() => handleSortClick('title', false)}
							>
								{i18n('tickets.list.sort.subject')} - {i18n('tickets.list.sort.descending')}
							</MenuItem>

							<MenuItem
								selected={sort.key === 'creationDate' && sort.asc}
								onClick={() => handleSortClick('creationDate', true)}
							>
								{i18n('tickets.list.sort.creationDate')} - {i18n('tickets.list.sort.ascending')}
							</MenuItem>

							<MenuItem
								selected={sort.key === 'creationDate' && !sort.asc}
								onClick={() => handleSortClick('creationDate', false)}
							>
								{i18n('tickets.list.sort.creationDate')} - {i18n('tickets.list.sort.descending')}
							</MenuItem>

							<MenuItem
								selected={sort.key === 'lastChangeDate' && sort.asc}
								onClick={() => handleSortClick('lastChangeDate', true)}
							>
								{i18n('tickets.list.sort.lastChangeDate')} - {i18n('tickets.list.sort.ascending')}
							</MenuItem>

							<MenuItem
								selected={sort.key === 'lastChangeDate' && !sort.asc}
								onClick={() => handleSortClick('lastChangeDate', false)}
							>
								{i18n('tickets.list.sort.lastChangeDate')} - {i18n('tickets.list.sort.descending')}
							</MenuItem>
						</StyledMenu>
					</Box>

					{!isMobile && (
						<Button
							variant="contained"
							startIcon={<AddCircleIcon />}
							onClick={() => navigate('/tickets/new')}
						>
							{i18n('tickets.new')}
						</Button>
					)}
				</Box>

				<Stack direction="row" spacing={1} flexWrap={'wrap'}>
					{FIXED_TICKET_STATES.map((status) => (
						<Button
							key={status.id}
							variant={filters?.some((f) => f.value === status.name) ? 'contained' : 'outlined'}
							color={
								filters?.some((f) => f.value === status.name)
									? (status.color as TicketStatus['color'])
									: 'inherit'
							}
							size="small"
							onClick={() => {
								handleFilterClick(status);
							}}
						>
							{status.name}
						</Button>
					))}
				</Stack>
				{isTaugorCompany && (
					<ToggleButtonGroup
						color="primary"
						exclusive
						onChange={handleChangeTickets}
						value={changeTickets}
						size="small"
						sx={{ mt: 2 }}
					>
						<ToggleButton value="AllTickets">{i18n('tickets.all')}</ToggleButton>
						<ToggleButton value="UserTickets">{i18n('tickets.myTickets')}</ToggleButton>
					</ToggleButtonGroup>
				)}
			</Box>

			{loading ? (
				<List>
					{new Array(5).fill(null).map((ticket, i) => (
						<React.Fragment key={i}>
							<Divider />

							<TicketsListItem ticket={ticket as NewTicketData} />
						</React.Fragment>
					))}
				</List>
			) : visibleTickets.length > 0 ? (
				<List>
					{visibleTickets.map((ticket) => (
						<React.Fragment key={ticket.ticketId}>
							<Divider />

							<TicketsListItem ticket={ticket as unknown as NewTicketData} />
						</React.Fragment>
					))}
				</List>
			) : (
				<Box
					sx={{
						p: 2,
					}}
				>
					<Typography textAlign="center">
						{tickets.length > 0 ? (
							<>
								{i18n('tickets.list.notFound')}
								<br />
								<br />
								{i18n('tickets.list.notFound.useButtonPrefix')}
								{' ('}
								<FilterIcon
									sx={{
										verticalAlign: 'middle',
									}}
								/>
								{') '}
								{i18n('tickets.list.notFound.useButtonSuffix')}
							</>
						) : (
							i18n('tickets.list.empty')
						)}
					</Typography>
				</Box>
			)}

			{isMobile && (
				<Tooltip title={i18n('tickets.new')}>
					<Fab
						size="medium"
						color="primary"
						sx={{
							position: 'fixed',
							bottom: ({ spacing }) => spacing(2),
							right: ({ spacing }) => spacing(2),
						}}
						onClick={() => navigate('/tickets/new')}
					>
						<AddIcon />
					</Fab>
				</Tooltip>
			)}

			{hasMore && isTaugorCompany && changeTickets === 'AllTickets' && (
				<Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
					<Button variant="contained" color="primary" onClick={handleLoadMore} disabled={loading}>
						{loading ? (
							'Carregando...'
						) : (
							<>
								<KeyboardArrowDown />
								<span>carregar mais</span>
							</>
						)}
					</Button>
				</Box>
			)}
		</Paper>
	);
};
